import { useEffect, useState } from "react";
import { api, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { Plus, Calendar, MapPin, Check, X, HelpCircle, Trash2 } from "lucide-react";

const ORGANIZER_ROLES = ["chief","deputy_chief","head_councillor","executive_director","band_registry_clerk"];
const RSVP_ROLES = ["chief","deputy_chief","head_councillor","councillor"];

export default function Sessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const canOrganize = ORGANIZER_ROLES.includes(user?.role) || user?.is_super_admin;
  const canRsvp = RSVP_ROLES.includes(user?.role) || user?.is_super_admin;
  const confirm = useConfirm();

  const load = () => api.get("/sessions").then(r=>setSessions(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const now = new Date();
  const upcoming = sessions.filter(s => new Date(s.scheduled_at) >= now);
  const past = sessions.filter(s => new Date(s.scheduled_at) < now);

  const rsvp = async (id, status) => {
    try {
      await api.post(`/sessions/${id}/rsvp?status=${status}`);
      toast.success(`RSVP recorded: ${status.toUpperCase()}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "RSVP failed"); }
  };
  const del = async (id) => {
    const ok = await confirm({ title: "Cancel session?", destructive: true, confirmLabel: "Cancel session" });
    if (!ok) return;
    try {
      await api.delete(`/sessions/${id}`);
      toast.success("Session cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono text-[#d4b876]">COUNCIL SESSIONS</div>
          <h1 className="font-display text-4xl mt-2">Session scheduler</h1>
          <p className="text-white/60 mt-2 max-w-2xl">Chief & Council session calendar. Councillors RSVP publicly; agenda items are transparent to all verified members.</p>
        </div>
        {canOrganize && (
          <button onClick={()=>setShowCreate(true)} className="btn-cta primary" data-testid="new-session-btn"><Plus size={12}/> Schedule session</button>
        )}
      </div>

      <h2 className="font-display text-2xl mt-10">Upcoming ({upcoming.length})</h2>
      <div className="mt-4 space-y-3">
        {upcoming.length === 0 && <div className="font-mono text-xs text-white/40">No upcoming sessions scheduled.</div>}
        {upcoming.map(s => <SessionCard key={s.id} s={s} canRsvp={canRsvp} onRsvp={rsvp} onDelete={del} currentUserId={user.id} />)}
      </div>

      {past.length > 0 && (
        <>
          <h2 className="font-display text-2xl mt-10 text-white/50">Past</h2>
          <div className="mt-4 space-y-3">
            {past.map(s => <SessionCard key={s.id} s={s} canRsvp={false} onRsvp={rsvp} onDelete={del} currentUserId={user.id} isPast />)}
          </div>
        </>
      )}

      {showCreate && <CreateSession onClose={()=>setShowCreate(false)} onCreated={load} />}
    </div>
  );
}

function SessionCard({ s, canRsvp, onRsvp, onDelete, currentUserId, isPast }) {
  const dt = new Date(s.scheduled_at);
  const yes = s.rsvps.filter(r=>r.status==="yes").length;
  const no = s.rsvps.filter(r=>r.status==="no").length;
  const maybe = s.rsvps.filter(r=>r.status==="maybe").length;
  return (
    <div className={`panel p-5 ${isPast ? "opacity-60" : ""}`} data-testid={`session-${s.id}`}>
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] text-[#89b4fa] tracking-widest uppercase flex items-center gap-2">
            <Calendar size={12}/> {dt.toLocaleString("en-CA", { weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
          </div>
          <div className="font-display text-xl mt-1">{s.title}</div>
          <div className="font-mono text-xs text-white/50 mt-1 flex items-center gap-2"><MapPin size={10}/> {s.location}</div>
          {s.agenda && <div className="text-white/70 text-sm mt-3 whitespace-pre-wrap max-w-3xl">{s.agenda}</div>}
          <div className="font-mono text-[10px] text-white/40 mt-3">Scheduled by {s.created_by_name} · {ROLE_LABEL[s.created_by_role]}</div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {s.created_by === currentUserId && (
            <button onClick={()=>onDelete(s.id)} className="text-[#ef5b6b] hover:text-white" data-testid={`del-session-${s.id}`}><Trash2 size={14}/></button>
          )}
          <div className="flex gap-2 font-mono text-[10px]">
            <span className="text-[#89b4fa]">✓ {yes}</span>
            <span className="text-[#d4b876]">? {maybe}</span>
            <span className="text-[#ef5b6b]">✕ {no}</span>
          </div>
          {canRsvp && (
            <div className="flex gap-1">
              {[["yes",Check,"#89b4fa"],["maybe",HelpCircle,"#d4b876"],["no",X,"#ef5b6b"]].map(([st, Icon, c]) => (
                <button key={st} onClick={()=>onRsvp(s.id, st)}
                  className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-widest ${s.my_rsvp===st ? "bg-white text-black border-white" : "border-white/20 text-white/70"}`}
                  style={s.my_rsvp!==st?{color:c, borderColor: `${c}66`}:{}}
                  data-testid={`rsvp-${s.id}-${st}`}>
                  <Icon size={10} className="inline mr-1"/>{st}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {s.rsvps.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10 font-mono text-[10px] text-white/50 flex flex-wrap gap-x-4 gap-y-1">
          {s.rsvps.map((r,i) => (
            <span key={i}>
              <span style={{color: r.status==="yes"?"#89b4fa":r.status==="maybe"?"#d4b876":"#ef5b6b"}}>[{r.status.toUpperCase()}]</span> {r.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateSession({ onClose, onCreated }) {
  const [f, setF] = useState({ title:"", scheduled_at:"", location:"Kasabonika Lake Band Office", agenda:"", is_public: true });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const payload = { ...f, scheduled_at: new Date(f.scheduled_at).toISOString() };
      await api.post("/sessions", payload);
      toast.success("Session scheduled");
      onCreated(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg border border-white/20 bg-[#181825] p-6 space-y-4">
        <div className="label-mono text-[#d4b876]">SCHEDULE COUNCIL SESSION</div>
        <input required placeholder="Session title" value={f.title} onChange={e=>setF({...f, title:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="session-title" />
        <div>
          <label className="label-mono block mb-1">Date & Time</label>
          <input required type="datetime-local" value={f.scheduled_at} onChange={e=>setF({...f, scheduled_at:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="session-datetime" />
        </div>
        <input placeholder="Location" value={f.location} onChange={e=>setF({...f, location:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="session-location" />
        <textarea rows={5} placeholder="Agenda items" value={f.agenda} onChange={e=>setF({...f, agenda:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="session-agenda" />
        <label className="flex items-center gap-2 font-mono text-xs">
          <input type="checkbox" checked={f.is_public} onChange={e=>setF({...f, is_public:e.target.checked})} data-testid="session-public"/>
          Publicly visible on landing countdown
        </label>
        {err && <div className="text-[#ef5b6b] font-mono text-xs">{err}</div>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={saving} className="btn-cta primary" data-testid="submit-session-btn">{saving?"Scheduling...":"Schedule"}</button>
        </div>
      </form>
    </div>
  );
}
